import { useAppSelector } from 'careand-redux/store';
import React, { FC, ReactNode, useState } from 'react';
import {
  getHealthSummary,
  SUMMARY_SECTION_DATA,
  SummaryData,
  HealthSummarySection,
  Sections,
  SummaryEntry,
} from './model';
import './style.scss';
import TopBar from 'scenes/HomePatient2/TopBar';
import { NoListContent } from 'components/HealthRecords/Appointments/PatientAppointmentList';
import SmoothCollapse from 'components/Utils/SmoothCollapse';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { downloadBlob } from 'services/FileDownloadUtils';
import { api } from 'careand-redux/api/ApiThunks';
import emptyHrImage from 'assets/img/empty-hr.webp';
import { DispatchOptions, useAppDispatch } from 'careand-redux/useAppDispatch';
import DefaultModal from 'components/Utils/DefaultModal';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview';

export default () => {
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const patientData = useAppSelector(
    (state) => activeUserId && state.healthRecords.byId[activeUserId],
  );
  const summaryData = patientData && getHealthSummary(patientData);

  if (!summaryData) {
    return null;
  }

  const sections = Object.keys(SUMMARY_SECTION_DATA) as HealthSummarySection[];

  const cards = sections
    .filter((section) => {
      return Object.keys(summaryData[section]).length > 0;
    })
    .map((section) => {
      const { title, icon } = SUMMARY_SECTION_DATA[section];
      const data = summaryData[section];
      let children: ReactNode | null = null;
      if (section === 'immunizations') {
        children = <ExportImmunizationsButton light />;
      }
      return (
        <SummaryCard title={title} icon={icon} data={data}>
          {children}
        </SummaryCard>
      );
    });

  return (
    <div className="hr-summary-page page">
      <TopBar title="Summary" />
      <div className="page-content">
        {cards.length === 0 ? (
          <NoListContent text="No records to show" image={emptyHrImage} />
        ) : (
          cards
        )}
      </div>
    </div>
  );
};

interface SummaryCardProps {
  icon: string;
  title: string;
  data: SummaryData;
  children?: ReactNode;
}

export const ExportImmunizationsButton = ({ light }: { light?: boolean }) => {
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const dispatch = useAppDispatch();

  const onClick = () => {
    setLoading(true);
    downloadBlob(
      api.HealthRecords.exportImmunizations,
      { patientId: activeUserId! },
      { filename: 'immunizations.pdf' },
    ).finally(() => setLoading(false));
  };

  function getFile<T extends DispatchOptions>(dispatchOptions: T) {
    return dispatch(
      api.HealthRecords.exportImmunizations({ patientId: activeUserId! }),
      dispatchOptions,
    );
  }

  return (
    <>
      <div className="mobile-only">
        <DefaultButton
          className={light ? 'light' : ''}
          name="Export Immunizations"
          icon="file-pdf"
          loading={loading}
          onClick={onClick}
        />
      </div>
      <div className="desktop-only">
        <DefaultButton
          className={light ? 'light' : ''}
          name="Export Immunizations"
          icon="file-pdf"
          loading={loading}
          onClick={() => setShowModal(true)}
        />
      </div>
      {showModal && (
        <DefaultModal
          className="modal health-record-modal large-modal"
          wrap={true}
          padding={false}
          content={<LargeResourcePreview autoLoad={true} getFile={getFile} />}
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export const SummaryCard: FC<SummaryCardProps> = ({ icon, title, data, children }) => {
  if (Object.keys(data).length === 0) {
    return null;
  }

  return (
    <SmoothCollapse icon={icon} title={title}>
      <>
        {Array.isArray(data) ? <Entries data={data} /> : <WithSections data={data} />}
        {children && <div className="summary-card__extra">{children}</div>}
      </>
    </SmoothCollapse>
  );
};

function WithSections({ data }: { data: Sections<SummaryEntry[]> }) {
  return (
    <ul className="sections">
      {Object.keys(data).map((section) => {
        const entries = data[section];
        return (
          (entries && (
            <>
              <span className="section-title">{section}</span>
              <Entries data={entries} />
            </>
          )) ||
          null
        );
      })}
    </ul>
  );
}

function Entries({ data }: { data: SummaryEntry[] }) {
  return (
    <ol className="entries">
      {data.map((it, idx) => (
        <Entry entry={it} key={idx} />
      ))}
    </ol>
  );
}

function Entry({ entry }: { entry: SummaryEntry }) {
  return (
    <li>
      <span>{entry.title}</span>
      {entry.subtitle && (
        <>
          <span>&nbsp;-&nbsp;</span>
          <span>{entry.subtitle}</span>
        </>
      )}
    </li>
  );
}
